import { derived, get, writable } from 'svelte/store';
import { getLocalStorage, removeLocalStorage, setLocalStorage } from '../utils/local-storage';
import { objectEntries, objectKeys } from '../utils/object';
import { region, type Region } from '../region';

/**
 * Cart (ID)
 */
const CART_KEYS = {
  us: '__hey-harper-us-cart__',
  uk: '__hey-harper-uk-cart__',
  eu: '__hey-harper-eu-cart__'
};

type CartId = Record<Region, string | null>;

// carts is a custom store that saves an object with a cart ID per region.
// each ID is saved in localStorage.
const cartIds = (() => {
  const initial: CartId = objectKeys(CART_KEYS).reduce(
    (acc, region) => {
      const id = getLocalStorage(CART_KEYS[region]);
      return id ? { ...acc, [region]: id } : acc;
    },
    { us: null, uk: null, eu: null }
  );

  const { subscribe, set, update } = writable<CartId>(initial);

  subscribe((carts) => {
    objectEntries(carts).forEach(([region, id]) => {
      if (id) {
        setLocalStorage(CART_KEYS[region], id);
      } else {
        removeLocalStorage(CART_KEYS[region]);
      }
    });
  });

  return {
    subscribe,
    set,
    update
  };
})();

export const cartId = derived([region, cartIds], ([$region, $cartIds]) => $cartIds[$region]);

export const deleteCartId = () => {
  cartIds.update((carts) => ({ ...carts, [get(region)]: null }));
};

export const updateCartId = (id: string) => {
  const currentId = get(cartId);

  if (currentId !== id) {
    cartIds.update((carts) => ({ ...carts, [get(region)]: id }));
  }
};
